import posthogClient from 'posthog-js';

import type { PostHogBootstrapData } from '../getPostHogBootstrapData';
import type { AnalyticsKeyValue, PosthogAnalyticsService } from './types';

import { waitWithTimeout } from '../wait-with-timeout';

const POSTHOG_LOAD_TIMEOUT = 5000; // 5 seconds

let posthogReadyResolve: (() => void) | null = null;

const posthogReady = new Promise<void>((resolve) => {
  posthogReadyResolve = resolve;
});

const isPosthogEnabled = () =>
  Boolean(process.env.NEXT_PUBLIC_POSTHOG_API_KEY && process.env.NEXT_PUBLIC_POSTHOG_API_HOST);

const initPosthog = (postHogBootstrapData?: PostHogBootstrapData | null) => {
  if (!isPosthogEnabled() || typeof window === 'undefined') {
    return;
  }

  const { initialCalculatedDevice, ...bootstrap } = postHogBootstrapData ?? {};

  posthogClient.init(process.env.NEXT_PUBLIC_POSTHOG_API_KEY as string, {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_API_HOST,
    bootstrap,
    autocapture: {
      css_selector_allowlist: ['[data-ph-capture-attribute-tracking-id]'],
    },
    save_referrer: true,
    loaded: () => {
      posthogReadyResolve?.();
    },
  });

  if (initialCalculatedDevice) {
    posthogClient.setPersonProperties({
      initial_calculated_device: initialCalculatedDevice,
    });
  }
};

const trackPosthogEvent = (name: string, props?: AnalyticsKeyValue) => {
  if (!isPosthogEnabled()) {
    return;
  }

  posthogClient.capture(name, props);
};

const identifyPosthogUser = (userId: string, props?: AnalyticsKeyValue) => {
  if (!isPosthogEnabled()) {
    return;
  }

  posthogClient.identify(userId, props);
};

const setPosthogUserProperties = (props: AnalyticsKeyValue) => {
  if (!isPosthogEnabled()) {
    return;
  }

  posthogClient.setPersonProperties(props);
};

const getPosthogSessionUrl = () => {
  if (!isPosthogEnabled()) {
    return;
  }

  return `${process.env.NEXT_PUBLIC_POSTHOG_API_HOST}/replay/${posthogClient?.sessionManager?.checkAndGetSessionAndWindowId().sessionId}`;
};

const getPosthogSessionId = () => {
  if (!isPosthogEnabled()) {
    return;
  }

  return posthogClient.get_session_id();
};

const setPosthogEventsProperties = (props: AnalyticsKeyValue) => {
  if (!isPosthogEnabled()) {
    return;
  }

  posthogClient.register(props);
};

const aliasPosthogUser = (aliasId: string, originalId: string) => {
  if (!isPosthogEnabled()) {
    return;
  }

  posthogClient.alias(aliasId, originalId);
};

const getPosthogDistinctId = () => {
  if (!isPosthogEnabled()) {
    return;
  }

  return posthogClient.get_distinct_id();
};

const waitUntilPosthogLoaded = async () => {
  if (!isPosthogEnabled()) {
    return;
  }

  return waitWithTimeout(posthogReady, POSTHOG_LOAD_TIMEOUT);
};

const getPosthogFeatureFlag = async (flagName: string): Promise<string | boolean | undefined> => {
  if (!isPosthogEnabled()) {
    return;
  }

  try {
    await waitUntilPosthogLoaded();
    return posthogClient.getFeatureFlag(flagName);
  } catch (error) {
    console.error('Error getting PostHog feature flag:', error);
    return;
  }
};

export const posthog: PosthogAnalyticsService = {
  init: initPosthog,
  isEnabled: isPosthogEnabled,
  trackEvent: trackPosthogEvent,
  identifyUser: identifyPosthogUser,
  setUserProperties: setPosthogUserProperties,
  getSessionId: getPosthogSessionId,
  getSessionUrl: getPosthogSessionUrl,
  setEventsProperties: setPosthogEventsProperties,
  aliasUser: aliasPosthogUser,
  getDistinctId: getPosthogDistinctId,
  waitUntilLoaded: waitUntilPosthogLoaded,
  getFeatureFlag: getPosthogFeatureFlag,
};
